/**
 * Meta (Facebook Pages + Instagram) connect contract.
 *
 * Pure logic only — no React, no network — so the shapes machine sends and the rules the card
 * renders can be tested directly. The component consumes these; it does not re-derive them.
 */

/**
 * Meta reaches Instagram through two products, and a brand may only have one of them:
 *
 *  - `facebook_login`  — Instagram account linked to a Facebook Page. The only path that can LIKE.
 *  - `instagram_login` — standalone Instagram professional account, no Page. No like.
 *
 * They are not interchangeable, which is why the card offers two buttons rather than one.
 */
export type MetaAuthType = 'facebook_login' | 'instagram_login';

export type MetaCapability = 'hide' | 'delete' | 'reply' | 'dm' | 'like';

/** One connected Page or Instagram account, as machine's `publicMetaChannels` sends it. */
export interface MetaChannel {
	platform: 'facebook' | 'instagram';
	channelId: string;
	handle: string;
	name: string;
	pictureUrl: string | null;
	category: string | null;
	capabilities: MetaCapability[];
	credentialId: string;
	authType: MetaAuthType | null;
	/**
	 * The token is inside its refresh window or already past it, and only a fresh consent fixes it.
	 *
	 * Machine DERIVES this per request rather than storing it, so it is always current — and the
	 * card dropped it on the floor until 2026-08-27. An expired credential is invisible to the
	 * customer that way: the channel renders healthy, its capability chips still list everything it
	 * was granted, and the only symptom is the agent quietly not acting.
	 */
	needsReconnect: boolean;
}

export const META_AUTH_OPTIONS: { authType: MetaAuthType; label: string; helper: string }[] = [
	{
		authType: 'facebook_login',
		label: 'Continue with Facebook',
		helper: 'For Instagram accounts linked to a Facebook Page. Connects the Page and the Instagram account together, and is the only option that can like comments.'
	},
	{
		authType: 'instagram_login',
		label: 'Continue with Instagram',
		helper: 'For an Instagram professional account with no Facebook Page. Can hide, delete, reply and DM — Meta has no like permission on this route.'
	}
];

/**
 * Narrow the server's list of offerable routes.
 *
 * Machine decides this from `META_ENABLED_AUTH_TYPES` and which app credentials exist, so the card
 * must not second-guess it — a button we render for a route machine has switched off returns 500.
 * Unknown values are dropped rather than trusted, and the result keeps the canonical order so the
 * buttons do not reshuffle between environments.
 */
export const metaAuthOptionsFor = (availableAuthTypes: MetaAuthType[]): typeof META_AUTH_OPTIONS =>
	META_AUTH_OPTIONS.filter((option) => availableAuthTypes.includes(option.authType));

/** Parse `availableAuthTypes` off the wire, discarding anything this build does not know. */
export const readMetaAuthTypes = (value: unknown): MetaAuthType[] => {
	if (!Array.isArray(value)) return [];
	return value.filter((entry): entry is MetaAuthType => entry === 'facebook_login' || entry === 'instagram_login');
};

/** Shown as chips on each connected account. Order is fixed so the row reads the same every time. */
export const META_CAPABILITY_LABELS: Record<MetaCapability, string> = {
	hide: 'Hide',
	delete: 'Delete',
	reply: 'Reply',
	dm: 'DM',
	like: 'Like'
};

const CAPABILITY_ORDER: MetaCapability[] = ['hide', 'delete', 'reply', 'dm', 'like'];

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');
const asStringOrNull = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

const asCapabilities = (value: unknown): MetaCapability[] => {
	if (!Array.isArray(value)) return [];
	const granted = new Set(value.filter((entry): entry is string => typeof entry === 'string'));
	return CAPABILITY_ORDER.filter((capability) => granted.has(capability));
};

const asAuthType = (value: unknown): MetaAuthType | null => {
	if (value === 'facebook_login' || value === 'instagram_login') return value;
	return null;
};

/**
 * Read the channel list off the integrations row.
 *
 * machine strips secrets before this reaches the browser (`publicMetaChannels`), so the card reads
 * from the integrations query it already polls rather than issuing a second request. Anything
 * malformed is dropped rather than rendered as a half-blank row.
 */
export const readMetaChannels = (authMetaData: Record<string, unknown> | undefined): MetaChannel[] => {
	const raw = authMetaData?.channels;
	if (!Array.isArray(raw)) return [];

	return raw
		.filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
		.map((entry) => ({
			platform: entry.platform === 'facebook' ? ('facebook' as const) : ('instagram' as const),
			channelId: asString(entry.channel_id),
			handle: asString(entry.handle),
			name: asString(entry.name),
			pictureUrl: asStringOrNull(entry.picture_url),
			category: asStringOrNull(entry.category),
			capabilities: asCapabilities(entry.capabilities),
			credentialId: asString(entry.credential_id),
			authType: asAuthType(entry.auth_type),
			// Defaults FALSE, deliberately. An older machine that does not send the field yet, or a
			// malformed value, must not paint every healthy channel with a reconnect warning — a
			// banner on everything is one nobody reads when it finally matters.
			needsReconnect: entry.needs_reconnect === true
		}))
		.filter((channel) => channel.channelId !== '');
};

/** Group for display: the settings list shows Facebook Pages and Instagram accounts separately. */
export const groupMetaChannels = (
	channels: MetaChannel[]
): { platform: 'facebook' | 'instagram'; label: string; channels: MetaChannel[] }[] =>
	(
		[
			{ platform: 'facebook' as const, label: 'Facebook Pages' },
			{ platform: 'instagram' as const, label: 'Instagram accounts' }
		]
	)
		.map((group) => ({ ...group, channels: channels.filter((c) => c.platform === group.platform) }))
		.filter((group) => group.channels.length > 0);

/**
 * Subtitle on the integrations card. One account shows its handle; several show a count, because
 * naming one of five is arbitrary and the count is what the operator actually wants to know.
 */
export const describeMetaConnection = (authMetaData: Record<string, unknown> | undefined): string | undefined => {
	const channels = readMetaChannels(authMetaData);
	if (channels.length === 0) return undefined;
	if (channels.length === 1) return channels[0]?.handle || undefined;
	return `${channels.length} accounts`;
};

/**
 * A channel with no capabilities is connected but useless — the person who authorized lacked the
 * Page tasks, or unticked the permissions on Meta's consent screen. Surfacing it is the whole
 * point of storing capabilities: the alternative is a rule that silently never fires.
 */
export const hasNoCapabilities = (channel: MetaChannel): boolean => channel.capabilities.length === 0;

/**
 * Ranked above the capability chips when both apply.
 *
 * A channel can be short of permissions AND have a dying token, and they call for opposite actions:
 * one is re-authorising with more ticked, the other is re-authorising at all. The expiry is the one
 * with a deadline, so it wins the row.
 */
export const needsReconnect = (channel: MetaChannel): boolean => channel.needsReconnect;

/**
 * Whether reconnecting via Facebook would gain this account anything.
 *
 * Only Instagram accounts connected through Instagram Login can gain `like`, and only by being
 * reconnected through a Page. Prompting anywhere else would be noise.
 */
export const canGainLikeViaFacebook = (channel: MetaChannel): boolean =>
	channel.platform === 'instagram' &&
	channel.authType === 'instagram_login' &&
	!channel.capabilities.includes('like');

/** The shape `findMetaChannels` needs off an integrations row; kept minimal so callers can pass any list. */
export interface MetaIntegrationRowLike {
	name?: string;
	authMetaData?: Record<string, unknown>;
}

/**
 * The connected Meta channels out of the account's integrations list.
 *
 * The list carries at most one `meta` row, matched by name rather than id because the id flips
 * from the catalogue id to the DB id on connect. No row, or a row with nothing connected, is an
 * empty list — never undefined — so callers can map over it without a guard.
 */
export const findMetaChannels = (integrations: MetaIntegrationRowLike[] | undefined): MetaChannel[] => {
	const metaRow = (integrations ?? []).find((row) => row.name?.toLowerCase() === 'meta');
	return readMetaChannels(metaRow?.authMetaData);
};
